import { NotificationMenuItem } from '@/components/notifications/notification-menu-item'
import { useUnreadNotificationCount } from '@/hooks/use-notifications'
import { useNotificationsSse } from '@/hooks/use-notifications-sse'
import { ProductInviteDialog } from '@/components/invite/product-invite-dialog'
import { getProductInviteConfig, shouldShowProductInviteHeaderButton } from '@/constants/product-invite'
import { ReactNode, useState } from "react"
import {
  User,
  UserPlus,
  Briefcase,
  Globe2,
  Code,
  Users,
  Plane,
  HeartPulse,
  Stethoscope,
  Handshake,
  Headset,
  Laptop2,
  Crown,
  LogIn,
  ChevronsUpDown,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuRadioGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import { SidebarGlyphSlot } from "@/components/sidebar/sidebar-glyph-slot"
import { useSidebar } from "@/components/ui/sidebar-context"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Badge } from "@/components/ui/badge"
import { InitialsAvatar } from "@/components/ui/initials-avatar"
import { LoginDialog } from "@/components/login-dialog"
import { AccountMenuIdentityHeader } from "@/components/account/account-menu-identity-header"
import { AccountMenuItems } from "@/components/account/account-menu-items"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { CURRENT_CLIENT, isProductFeedbackEnabled } from "@/constants"
import { getUpgradePlanConfig, shouldShowEmployerPlanBadge } from "@/constants/upgrade-plan"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import { useOrganisationBilling } from "@/hooks/use-plans"
import { resolveFocusSwitchOptions } from "@/lib/focus-switch-options"
import type { GroupInfo, UserFocus } from "@/hooks/use-focus-store"

const getGroupIcon = (groupName: string): ReactNode => {
  const PatientIcon = () => (
    <span className="relative inline-flex">
      <User className="size-4" />
      <HeartPulse className="absolute right-0 bottom-0 size-2 text-red-500" />
    </span>
  )
  switch (groupName) {
    case "candidate":
      return <User className="size-4" />
    case "employer":
      return <Briefcase className="size-4" />
    case "recruiter":
      return <Users className="size-4" />
    case "platform":
      return <Globe2 className="size-4" />
    case "developer":
      return <Code className="size-4" />
    case "traveler":
      return <Plane className="size-4" />
    case "agent":
      return <Headset className="size-4" />
    case "patient":
      return <PatientIcon />
    case "practitioner":
      return <Stethoscope className="size-4" />
    case "client":
      return <Handshake className="size-4" />
    case "engineer":
      return <Laptop2 className="size-4" />
    case "principal":
      return <Crown className="size-4" />
    case "operator":
      return <Headset className="size-4" />
    default:
      return <User className="size-4" />
  }
}

const getFocusIcon = (focus: string): ReactNode => {
  const PatientIcon = ({ className = "size-4" }) => (
    <span className="relative inline-flex">
      <User className={className} />
      <HeartPulse className="absolute right-0 bottom-0 size-2 text-red-500" />
    </span>
  )
  switch (focus) {
    case "candidate":
      return <User className="size-4" />
    case "employer":
      return <Briefcase className="size-4" />
    case "recruiter":
      return <Users className="size-4" />
    case "platform":
      return <Globe2 className="size-4" />
    case "developer":
      return <Code className="size-4" />
    case "traveler":
      return <Plane className="size-4" />
    case "agent":
      return <Headset className="size-4" />
    case "patient":
      return <PatientIcon />
    case "practitioner":
      return <Stethoscope className="size-4" />
    case "client":
      return <Handshake className="size-4" />
    case "engineer":
      return <Laptop2 className="size-4" />
    case "principal":
      return <Crown className="size-4" />
    case "operator":
      return <Headset className="size-4" />
    default:
      return <User className="size-4" />
  }
}

const toTitle = (s: string) =>
  (s || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

interface UserMenuProps {
  isAuthenticated: boolean
  /** Person name (e.g. first + last); falls back to email when empty. */
  personName?: string | null
  email?: string | null
  avatarUrl?: string | null
  avatarColorSeed?: string
  avatarUseSilhouette?: boolean
  logout: () => void
  groups: GroupInfo[]
  focus: UserFocus | null
  updateGroups: (
    updates: Record<string, boolean>
  ) => Promise<
    | { current_focus: string | null; available_foci: string[]; focus_confirmed: boolean }
    | undefined
  >
  setFocus: (focus: string, threadId?: string | null) => Promise<any>
  fetchUserData: () => Promise<void>
  isFetching: boolean
  menuLayout?: "horizontal" | "vertical"
  isCollapsed?: boolean
  showFocusSwitching?: boolean
  selfServiceLocked?: boolean
  roleSwitchingEnabled?: boolean
  showMemories?: boolean
  onNavigateToPath?: (path: string) => void
  onOpenSettings?: (path: string) => void
}

export function UserMenu({
  isAuthenticated,
  personName,
  email,
  avatarUrl,
  avatarColorSeed = "",
  avatarUseSilhouette: _avatarUseSilhouette = true,
  logout,
  groups,
  focus,
  updateGroups,
  setFocus,
  fetchUserData: _fetchUserData,
  isFetching,
  menuLayout = "horizontal",
  isCollapsed = false,
  showFocusSwitching = true,
  selfServiceLocked = false,
  roleSwitchingEnabled = true,
  showMemories = true,
  onNavigateToPath,
  onOpenSettings,
}: UserMenuProps) {
  const { isMobile, state: sidebarState } = useSidebar()
  const [menuOpen, setMenuOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const inviteConfig = getProductInviteConfig()
  const showInvite = isMobile && CURRENT_CLIENT.layout?.mobileInvitePlacement === 'settings-menu'
    && shouldShowProductInviteHeaderButton({ isAuthenticated }) && Boolean(inviteConfig)
  const upgradePlan = getUpgradePlanConfig()
  const employerOrg = useEmployerOrganisationOptional()
  const isEmployerFocus = focus?.current_focus === "employer"
  const organisationSlug = isEmployerFocus ? employerOrg?.activeOrganisationSlug ?? undefined : undefined
  const { data: billing } = useOrganisationBilling(organisationSlug)
  const planLabel = billing?.display_name?.trim() || null
  const showPlanBadge =
    shouldShowEmployerPlanBadge(upgradePlan, {
      isAuthenticated,
      isEmployerFocus,
      organisationSlug: employerOrg?.activeOrganisationSlug,
    }) && Boolean(planLabel)
  const notificationsInSettingsMenu =
    isMobile && CURRENT_CLIENT.layout?.mobileNotificationsPlacement === 'settings-menu'
  const { data: unreadNotifications = 0 } = useUnreadNotificationCount()
  useNotificationsSse(notificationsInSettingsMenu)
  const showAvatarNotificationDot = notificationsInSettingsMenu && unreadNotifications > 0

  const closeMenuAfterAction = () => {
    setMenuOpen(false)
  }

  const assignedGroups = groups.filter((g) => g.is_assigned)
  const assignedCount = assignedGroups.length
  const hasUnassignedRoles = groups.some((g) => !g.is_assigned)
  const focusSwitchOptions = resolveFocusSwitchOptions(groups, focus, selfServiceLocked)
  const showRolesMenu =
    showFocusSwitching &&
    (roleSwitchingEnabled || (!selfServiceLocked && (assignedCount > 1 || hasUnassignedRoles)))

  const currentFocus = focus?.current_focus ?? null
  const currentRoleGroup =
    (currentFocus ? groups.find((g) => g.name === currentFocus) : undefined) ??
    (currentFocus
      ? focusSwitchOptions.find((g) => g.name === currentFocus)
      : undefined) ??
    null
  const currentRoleLabel = currentRoleGroup
    ? toTitle(currentRoleGroup.display_name || currentRoleGroup.name)
    : currentFocus
      ? toTitle(currentFocus)
      : "Role"

  const handleGroupChange = async (group: GroupInfo, checked: boolean) => {
    if (selfServiceLocked) {
      return
    }
    if (!checked && assignedCount === 1 && group.is_assigned) {
      return
    }
    try {
      closeMenuAfterAction()
      await updateGroups({ [group.name]: checked })
    } catch (error) {
      console.error("UserMenu: Failed to update groups:", error)
    }
  }

  const footerTriggerClass = cn(
    "h-10 overflow-hidden border-0 group-data-[collapsible=icon]:!size-8",
    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
  )

  const footerTextClass = cn(
    "flex min-w-0 flex-1 items-center gap-2 leading-none",
    menuLayout === "vertical" ? "justify-center text-center" : "text-left"
  )

  const trimmedPersonName = personName?.trim() ?? ""
  const trimmedEmail = email?.trim() ?? ""
  const primaryLabel = isAuthenticated
    ? trimmedPersonName || trimmedEmail || "Account"
    : CURRENT_CLIENT.loginLabel
  const footerTooltip = isAuthenticated
    ? primaryLabel
    : (CURRENT_CLIENT.loginSubheader ?? CURRENT_CLIENT.loginLabel)

  const triggerButton = (
    <SidebarMenuButton
      type="button"
      menuLayout={menuLayout}
      className={footerTriggerClass}
      aria-label={
        showAvatarNotificationDot
          ? `${primaryLabel}, unread notifications`
          : primaryLabel
      }
    >
      {isAuthenticated ? (
        <SidebarGlyphSlot>
          <InitialsAvatar
            src={avatarUrl}
            colorSeed={avatarColorSeed || trimmedEmail || primaryLabel}
            className="size-8 shrink-0 border-0"
            alt={primaryLabel}
            showNotificationDot={showAvatarNotificationDot}
          />
        </SidebarGlyphSlot>
      ) : null}
      {!isCollapsed && (
        <div className={footerTextClass}>
          <span className="truncate text-sm font-medium text-sidebar-foreground">
            {primaryLabel}
          </span>
          {showPlanBadge ? (
            <Badge
              variant="outline"
              className="h-5 shrink-0 rounded-full px-1.5 text-[10px] font-medium leading-none tracking-wide text-muted-foreground"
            >
              {planLabel}
            </Badge>
          ) : null}
        </div>
      )}
      {!isCollapsed && (
        <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground" />
      )}
    </SidebarMenuButton>
  )

  const dropdownTrigger = isCollapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={sidebarState !== "collapsed" || isMobile}
      >
        {footerTooltip}
      </TooltipContent>
    </Tooltip>
  ) : (
    <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
  )

  if (!isAuthenticated) {
    return (
      <SidebarMenu className="h-full">
        <SidebarMenuItem className="h-full">
          <LoginDialog>
            <SidebarMenuButton
              menuLayout={menuLayout}
              tooltip={isCollapsed ? footerTooltip : undefined}
              className={footerTriggerClass}
            >
              <SidebarGlyphSlot>
                <LogIn className="size-4 shrink-0" />
              </SidebarGlyphSlot>
              {!isCollapsed && (
                <div className={footerTextClass}>
                  <span className="truncate text-xs text-muted-foreground">
                    {CURRENT_CLIENT.loginLabel}
                  </span>
                  {CURRENT_CLIENT.loginSubheader ? (
                    <span className="truncate text-xs text-muted-foreground">
                      {CURRENT_CLIENT.loginSubheader}
                    </span>
                  ) : null}
                </div>
              )}
            </SidebarMenuButton>
          </LoginDialog>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu className="h-full">
      <SidebarMenuItem className="h-full">
        <DropdownMenu
          modal={false}
          open={menuOpen}
          onOpenChange={setMenuOpen}
        >
          {dropdownTrigger}
          <DropdownMenuContent
            side="top"
            align="start"
            collisionPadding={12}
            className="w-[240px] max-h-[min(calc(100dvh-1.5rem),var(--radix-dropdown-menu-content-available-height))]"
            onCloseAutoFocus={(event) => {
              if (inviteOpen) event.preventDefault()
            }}
          >
            <AccountMenuIdentityHeader personName={trimmedPersonName} email={trimmedEmail} />
            {showRolesMenu && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    {currentFocus ? getFocusIcon(currentFocus) : <User className="size-4" />}
                    {currentRoleLabel}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {!selfServiceLocked && (
                      <>
                        <DropdownMenuLabel>My roles</DropdownMenuLabel>
                        {groups.map((group) => {
                          const isLastAssigned = group.is_assigned && assignedCount === 1
                          return (
                            <DropdownMenuCheckboxItem
                              key={group.name}
                              checked={group.is_assigned}
                              onCheckedChange={(checked) =>
                                handleGroupChange(group, checked === true)
                              }
                              onSelect={(e) => e.preventDefault()}
                              disabled={isFetching || (isLastAssigned && group.is_assigned)}
                            >
                              {getGroupIcon(group.name)}
                              {toTitle(group.display_name || group.name)}
                            </DropdownMenuCheckboxItem>
                          )
                        })}
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuRadioGroup
                      value={focus?.current_focus ?? ""}
                      onValueChange={(focusValue) => {
                        closeMenuAfterAction()
                        void setFocus(focusValue).catch((error) => {
                          console.error("UserMenu: Error setting focus:", error)
                          toast.error("Could not switch focus — try again")
                        })
                      }}
                    >
                      {focusSwitchOptions.map((group) => (
                        <DropdownMenuRadioItem
                          key={group.name}
                          value={group.name}
                          disabled={isFetching}
                          onSelect={(e) => e.preventDefault()}
                        >
                          {getFocusIcon(group.name)}
                          {toTitle(group.display_name || group.name)}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}
            {isMobile && CURRENT_CLIENT.layout?.mobileNotificationsPlacement === 'settings-menu' && (
              <NotificationMenuItem onSelect={() => {
                closeMenuAfterAction()
                onNavigateToPath?.('/notifications')
              }} />
            )}
            {showInvite && inviteConfig && (
              <DropdownMenuItem onSelect={() => {
                setMenuOpen(false)
                setInviteOpen(true)
              }}>
                <UserPlus aria-hidden />
                {inviteConfig.ctaButtonLabel}
              </DropdownMenuItem>
            )}
            <AccountMenuItems
              logout={logout}
              onClose={closeMenuAfterAction}
              onOpenSettings={onOpenSettings}
              onNavigateToPath={onNavigateToPath}
              showUpgrade={Boolean(upgradePlan)}
              showBilling
              showMemories={showMemories}
              showFeedback={isProductFeedbackEnabled()}
              showTheme
            />
          </DropdownMenuContent>
        </DropdownMenu>
        {showInvite && inviteConfig && (
          <ProductInviteDialog open={inviteOpen} onOpenChange={setInviteOpen}
            headline={inviteConfig.headline} description={inviteConfig.blurb}
            submitLabel={inviteConfig.ctaButtonLabel} />
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
